import React from 'react';
import { observer } from 'mobx-react-lite';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Chip, Box } from '@mui/material';
import { useRootStore } from '../../../store/contexts';
import { graphqlToTime } from '../../utils';
import type { ActivityEvent } from '../../stores/ActivityStore';

export const ActivityTab: React.FC = observer(() => {
  const rootStore = useRootStore();
  const activityStore = rootStore.activityStore;
  const langStore = rootStore.langStore;

  const columns: MRT_ColumnDef<ActivityEvent>[] = [
      {
        accessorKey: 'timestamp',
        header: langStore.monitorTimestamp,
        size: 180,
        Cell: ({ cell }) => {
          const date = new Date(cell.getValue<number>());
          // 13:45:35.120, con el helper de la hora del proyecto.
          return graphqlToTime(date) + '.' + date.getMilliseconds().toString().padStart(3, '0');
        },
      },
      {
        accessorKey: 'eventType',
        header: langStore.monitorEvent,
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          const color = value === 'START' ? 'info' : value === 'END' ? 'success' : 'warning';
          const label = value === 'START' ? langStore.monitorStart : value === 'END' ? langStore.monitorEnd : value;
          return <Chip label={label} color={color} size="small" />;
        },
      },
      {
        accessorKey: 'type',
        header: langStore.monitorType,
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          const color = value === 'query' ? 'default' : value === 'mutation' ? 'primary' : 'secondary';
          const label = value === 'query' ? langStore.monitorQuery : value === 'mutation' ? langStore.monitorMutation : value;
          return <Chip label={label} color={color} size="small" variant="outlined" />;
        },
      },
      {
        accessorKey: 'operation',
        header: langStore.monitorOperation,
        size: 200,
      },
      {
        accessorKey: 'duration',
        header: langStore.monitorDuration,
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<number | undefined>();
          if (!value) return langStore.monitorDash;
          const color = value > 3000 ? 'error' : value > 1000 ? 'warning' : 'success';
          return (
            <Chip
              label={`${value}${langStore.monitorMs}`}
              color={color}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        accessorKey: 'pathname',
        header: langStore.monitorPathname,
        size: 250,
        Cell: ({ cell }) => {
          const value = cell.getValue<string | undefined>();
          return value || langStore.monitorDash;
        },
      },
      {
        accessorKey: 'presentation',
        header: langStore.monitorPresentation,
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue<string | undefined>();
          return value ? (
            <Chip label={value} size="small" variant="outlined" />
          ) : (
            langStore.monitorDash
          );
        },
      },
    ];

  return (
    <Box sx={{ p: 2 }}>
      <MaterialReactTable
        columns={columns}
        data={activityStore.recentEvents}
        enableColumnResizing
        enableColumnOrdering
        enableDensityToggle
        enableFullScreenToggle={false}
        initialState={{
          density: 'compact',
          sorting: [{ id: 'timestamp', desc: true }],
        }}
        muiTableProps={{
          sx: {
            tableLayout: 'fixed',
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '0.75rem',
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            fontSize: '0.75rem',
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );
});
